/**
 * Module accent colours from the branding config. The wholesale module uses its own
 * colours, or the retail ones when its branding is set to "use the retail colours"
 * (enable_custom_wholesale_colors = false) - the same rule BrandingContext applies to the
 * CSS variables.
 */

export const retailAccent = (config) =>
  config?.retail_accent_color || config?.app_accent_color || config?.accent_color || '#D4AF37';

export const wholesaleAccent = (config) =>
  config?.enable_custom_wholesale_colors === false
    ? retailAccent(config)
    : config?.wholesale_accent_color ||
      config?.wholesale_secondary_color ||
      config?.app_accent_color ||
      config?.accent_color ||
      '#10B981';

/**
 * Sidebar colours.
 *
 * Each module's sidebar background either follows each user's theme, or is a set colour
 * blended with the user's theme: strength 100 is the set colour, 0 the theme's own. The
 * wholesale module uses the retail setting when it is set to the retail colours. Text,
 * border and hover colours are derived from the resulting background so the sidebar is
 * readable in every theme; the accent (active item, logo tile) is the brand's.
 */

// Each theme's surface colour (--color-bg-secondary in index.css)
export const THEME_SURFACES = {
  light: '#FFFFFF',
  charcoal: '#2C2C2C',
  midnight: '#1E293B',
  ocean: '#162033'
};

const THEME_SIDEBAR = {
  bg: 'var(--color-bg-secondary)',
  text: 'var(--color-text-primary)',
  border: 'var(--color-border)',
  hover: 'var(--color-bg-tertiary)'
};

const LIGHT_TEXT = '#F9FAFB';
const DARK_TEXT = '#1F2937';

const toRgb = (hex) => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(String(hex || '').trim());
  if (!match) return null;
  const digits =
    match[1].length === 3
      ? match[1]
          .split('')
          .map((c) => c + c)
          .join('')
      : match[1];
  return [0, 2, 4].map((i) => parseInt(digits.slice(i, i + 2), 16));
};

const toHex = (rgb) =>
  `#${rgb
    .map((v) => Math.round(v).toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;

/** `a` and `b` mixed: weight 1 is all `a`, 0 all `b` */
export const mixColours = (a, b, weight) => {
  const x = toRgb(a);
  const y = toRgb(b);
  if (!x || !y) return a;
  return toHex(x.map((v, i) => v * weight + y[i] * (1 - weight)));
};

const luminance = (hex) => {
  const [r, g, b] = toRgb(hex).map((v) => {
    const c = v / 255;
    return c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

const contrast = (a, b) => {
  const [hi, lo] = [luminance(a), luminance(b)].sort((p, q) => q - p);
  return (hi + 0.05) / (lo + 0.05);
};

/** The sidebar background setting a module uses: { mode, color, strength } */
export const sidebarBackground = (config, module) => {
  const own = module === 'wholesale' && config?.enable_custom_wholesale_colors !== false;
  const prefix = own ? 'wholesale' : 'retail';
  const strength = Number(config?.[`${prefix}_sidebar_bg_strength`]);
  return {
    mode: config?.[`${prefix}_sidebar_bg_mode`] === 'color' ? 'color' : 'theme',
    color: config?.[`${prefix}_sidebar_bg_color`] || (own ? '#047857' : '#1A1A1A'),
    strength: Number.isFinite(strength) ? Math.min(100, Math.max(0, strength)) : 100
  };
};

/** The sidebar's colours for a module in a theme: { bg, text, border, hover } */
export const sidebarColours = (config, module, themeId) => {
  const { mode, color, strength } = sidebarBackground(config, module);
  if (mode !== 'color' || !toRgb(color)) return THEME_SIDEBAR;
  const bg = mixColours(color, THEME_SURFACES[themeId] || THEME_SURFACES.light, strength / 100);
  const text = contrast(LIGHT_TEXT, bg) >= contrast(DARK_TEXT, bg) ? LIGHT_TEXT : DARK_TEXT;
  return {
    bg,
    text,
    border: mixColours(text, bg, 0.14),
    hover: mixColours(text, bg, 0.08)
  };
};
